import jsPDF from 'jspdf';

const IMAGE_LOAD_TIMEOUT_MS = 15000;
const COLS = 2;
const ROWS = 4;
const PER_PAGE = COLS * ROWS;

const formatPrice = (price) => {
  if (price === undefined || price === null || price === '') {
    return '';
  }

  const amount = Number(price);
  if (Number.isNaN(amount)) {
    return '';
  }

  return `Rs. ${amount.toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

const cleanText = (value) =>
  String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const withTimeout = (promise, ms = IMAGE_LOAD_TIMEOUT_MS) =>
  Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Image load timeout')), ms);
    }),
  ]);

const getProductImageUrl = (product) => {
  if (product?.photos?.length) {
    return product.photos[0];
  }
  return product?.photo || '';
};

const blobToImageData = (blob) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      if (typeof dataUrl !== 'string') {
        resolve(null);
        return;
      }

      const format = blob.type?.includes('png') ? 'PNG' : 'JPEG';
      const img = new Image();
      img.onload = () => {
        resolve({
          dataUrl,
          format,
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      };
      img.onerror = () => resolve({ dataUrl, format, width: 0, height: 0 });
      img.src = dataUrl;
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });

const fetchImageBlob = async (url) => {
  const response = await withTimeout(fetch(url));
  if (!response.ok) {
    return null;
  }
  return response.blob();
};

const loadImageData = async (url) => {
  if (!url) {
    return null;
  }

  const normalizedUrl = url.startsWith('/')
    ? `${window.location.origin}${url}`
    : url;

  const sources = [];

  if (normalizedUrl.includes('firebasestorage.googleapis.com')) {
    sources.push(normalizedUrl);
    sources.push(`/api/media/image?url=${encodeURIComponent(normalizedUrl)}`);
  } else if (url.startsWith('/')) {
    sources.push(url);
    sources.push(normalizedUrl);
  } else {
    sources.push(normalizedUrl);
  }

  for (const source of sources) {
    try {
      const blob = await fetchImageBlob(source);
      if (!blob || blob.size === 0) {
        continue;
      }
      const imageData = await blobToImageData(blob);
      if (imageData) {
        return imageData;
      }
    } catch (error) {
      console.warn('Image load failed:', source, error.message);
    }
  }

  return null;
};

const drawContainedImage = (pdf, imageData, boxX, boxY, boxW, boxH) => {
  pdf.setFillColor(248, 250, 252);
  pdf.setDrawColor(229, 231, 235);
  pdf.setLineWidth(0.15);
  pdf.rect(boxX, boxY, boxW, boxH, 'FD');

  if (!imageData?.dataUrl || !imageData.width || !imageData.height) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(156, 163, 175);
    pdf.text('No image', boxX + boxW / 2, boxY + boxH / 2, { align: 'center' });
    return;
  }

  const scale = Math.min(boxW / imageData.width, boxH / imageData.height);
  const drawW = imageData.width * scale;
  const drawH = imageData.height * scale;
  const drawX = boxX + (boxW - drawW) / 2;
  const drawY = boxY + (boxH - drawH) / 2;

  try {
    pdf.addImage(
      imageData.dataUrl,
      imageData.format,
      drawX,
      drawY,
      drawW,
      drawH,
      undefined,
      'FAST'
    );
  } catch (error) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(156, 163, 175);
    pdf.text('No image', boxX + boxW / 2, boxY + boxH / 2, { align: 'center' });
  }
};

const drawTextLines = (pdf, lines, x, startY, lineHeight) => {
  let y = startY;
  lines.forEach((line) => {
    pdf.text(line, x, y);
    y += lineHeight;
  });
  return y;
};

const drawFullPageImage = (pdf, imageData, pageWidth, pageHeight) => {
  if (!imageData?.dataUrl || !imageData.width || !imageData.height) {
    return;
  }

  const scale = Math.max(pageWidth / imageData.width, pageHeight / imageData.height);
  const drawW = imageData.width * scale;
  const drawH = imageData.height * scale;
  const drawX = (pageWidth - drawW) / 2;
  const drawY = (pageHeight - drawH) / 2;

  pdf.addImage(
    imageData.dataUrl,
    imageData.format,
    drawX,
    drawY,
    drawW,
    drawH,
    undefined,
    'FAST'
  );
};

export const exportCatalogPdf = async (products = []) => {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 16;
  const headerHeight = 22;
  const footerHeight = 10;
  const colGap = 10;
  const rowGap = 3;
  const contentTop = headerHeight + 3;
  const contentWidth = pageWidth - margin * 2;
  const contentHeight = pageHeight - contentTop - footerHeight - 4;
  const cardWidth = (contentWidth - colGap * (COLS - 1)) / COLS;
  const cardHeight = (contentHeight - rowGap * (ROWS - 1)) / ROWS;
  const productPages = Math.ceil(products.length / PER_PAGE);
  const totalPages = 1 + Math.max(productPages, 0);
  const dateLabel = new Date().toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  const [coverData, logoData] = await Promise.all([
    loadImageData('/CatalogFront.png'),
    loadImageData('/Aalap.png'),
  ]);

  drawFullPageImage(pdf, coverData, pageWidth, pageHeight);
  const images = await Promise.all(
    products.map((product) => loadImageData(getProductImageUrl(product)))
  );

  const drawHeader = () => {
    pdf.setFillColor(37, 99, 235);
    pdf.rect(0, 0, pageWidth, 3, 'F');

    if (logoData?.width && logoData?.height) {
      const logoMaxW = 34;
      const logoMaxH = 12;
      const logoScale = Math.min(logoMaxW / logoData.width, logoMaxH / logoData.height);
      pdf.addImage(
        logoData.dataUrl,
        logoData.format,
        margin,
        7,
        logoData.width * logoScale,
        logoData.height * logoScale
      );
    } else {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.setTextColor(37, 99, 235);
      pdf.text('Aalap Electronics', margin, 14);
    }

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.setTextColor(17, 24, 39);
    pdf.text('Product Catalog', pageWidth - margin, 12, { align: 'right' });
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(107, 114, 128);
    pdf.text(
      `${products.length} product${products.length === 1 ? '' : 's'} | ${dateLabel}`,
      pageWidth - margin,
      17,
      { align: 'right' }
    );

    pdf.setDrawColor(219, 234, 254);
    pdf.setLineWidth(0.4);
    pdf.line(margin, headerHeight, pageWidth - margin, headerHeight);
  };

  const drawFooter = (pageIndex) => {
    pdf.setDrawColor(229, 231, 235);
    pdf.setLineWidth(0.25);
    pdf.line(margin, pageHeight - footerHeight, pageWidth - margin, pageHeight - footerHeight);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(107, 114, 128);
    pdf.text(
      'Aalap Electronics | Swastik Cross Roads Corner, Ahmedabad 380009 | +91 96388 89898',
      margin,
      pageHeight - 5
    );
    pdf.text(`Page ${pageIndex} of ${totalPages}`, pageWidth - margin, pageHeight - 5, {
      align: 'right',
    });
  };

  const drawProductCard = (product, imageData, x, y) => {
    const pad = 1.5;
    const innerWidth = cardWidth - pad * 2;
    const priceBarHeight = 8;
    const textX = x + pad;
    const textWidth = innerWidth;
    const priceBarY = y + cardHeight - priceBarHeight;

    const bodyHeight = cardHeight - priceBarHeight - pad;
    const textZoneHeight = 10.5;
    const imageBoxHeight = Math.max(bodyHeight - textZoneHeight - 1, bodyHeight * 0.72);
    const imageBoxY = y + pad;

    pdf.setFillColor(255, 255, 255);
    pdf.setDrawColor(209, 213, 219);
    pdf.setLineWidth(0.25);
    pdf.roundedRect(x, y, cardWidth, cardHeight, 1.5, 1.5, 'FD');

    pdf.setFillColor(249, 250, 251);
    pdf.rect(x + 0.4, priceBarY, cardWidth - 0.8, priceBarHeight - 0.4, 'F');

    drawContainedImage(pdf, imageData, textX, imageBoxY, innerWidth, imageBoxHeight);

    let textY = imageBoxY + imageBoxHeight + 5;

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(7.5);
    pdf.setTextColor(17, 24, 39);
    const nameLines = pdf
      .splitTextToSize(cleanText(product.name) || 'Untitled product', textWidth)
      .slice(0, 2);
    textY = drawTextLines(pdf, nameLines, textX, textY, 3);

    const meta = [product.brandName || product.brand, product.categoryName || product.category]
      .filter(Boolean)
      .join(' | ');
    if (meta) {
      textY += 0.4;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(6);
      pdf.setTextColor(59, 130, 246);
      drawTextLines(
        pdf,
        [pdf.splitTextToSize(meta, textWidth)[0]],
        textX,
        textY,
        2.6
      );
    }

    const priceY = y + cardHeight - 2.5;
    const mrpText = product.mrp ? `MRP: ${formatPrice(product.mrp)}` : '';
    const offerText = product.offerPrice ? formatPrice(product.offerPrice) : '';

    if (mrpText) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(6.5);
      pdf.setTextColor(107, 114, 128);
      pdf.text(mrpText, textX, priceY);
    }

    if (offerText) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.setTextColor(22, 163, 74);
      pdf.text(offerText, x + cardWidth - pad, priceY, { align: 'right' });
    }
  };

  products.forEach((product, index) => {
    const pageIndex = Math.floor(index / PER_PAGE);
    const pos = index % PER_PAGE;

    if (pos === 0) {
      pdf.addPage();
      drawHeader();
      drawFooter(pageIndex + 2);
    }

    const col = pos % COLS;
    const row = Math.floor(pos / COLS);
    const x = margin + col * (cardWidth + colGap);
    const y = contentTop + row * (cardHeight + rowGap);

    drawProductCard(product, images[index], x, y);
  });

  const filename = `Aalap-Catalog-${new Date().toISOString().slice(0, 10)}.pdf`;
  pdf.save(filename);
  return filename;
};
